import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, Route, Routes, useLocation, useParams, useSearchParams } from "react-router-dom";
import { Banner, BrandMark, Card, Skeleton } from "@guestpilot/ui-kit";
import { resolveLocale, type LocaleCode } from "@guestpilot/i18n";
import { Markdown } from "../../components/Markdown";
import { I18nProvider, useI18n } from "../../i18n";
import { SUPPORT_EMAIL } from "../../lib/helpLinks";

/**
 * The public side of GuestPilot, what anyone sees at the address of the app outside Microsoft Teams (a browser, a Marketplace
 * reviewer, a prospect): what the product is, the privacy policy, the terms, the help pages, support, and the Microsoft Marketplace
 * page. No sign-in, no session, no API call: texts come from the i18n resources and from static files of the web app. These addresses are
 * the links given to the Teams Store and to the Marketplace listing.
 */
const LANGS: { code: LocaleCode; label: string }[] = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
];
export const HELP_TOPICS = ["setup", "permissions", "notifications", "retention", "faq"] as const;
type Topic = (typeof HELP_TOPICS)[number];
const SUPPORT = SUPPORT_EMAIL || "support@eruditego.fr";
const STORE_KEY = "gp-public-lang";

function initialLocale(search: string): LocaleCode {
  const asked = new URLSearchParams(search).get("lang");
  if (asked) return resolveLocale(asked);
  try {
    const saved = window.localStorage.getItem(STORE_KEY);
    if (saved) return resolveLocale(saved);
  } catch {
    /* storage can be blocked */
  }
  return resolveLocale(typeof navigator === "undefined" ? "en" : navigator.language);
}

/** The text of a static page, in the visitor's language, else English (and then it says so). */
async function loadText(paths: (l: string) => string, lang: string): Promise<{ text: string; lang: string }> {
  for (const l of lang === "en" ? ["en"] : [lang, "en"]) {
    const res = await fetch(paths(l));
    const type = res.headers.get("content-type") ?? "";
    if (res.ok && !type.includes("text/html")) return { text: await res.text(), lang: l };
  }
  throw new Error("page not available");
}

export function PublicSite() {
  const { pathname, search } = useLocation();
  const [locale, setLocale] = useState<LocaleCode>(() => initialLocale(search));
  // a help address carries its language: /help/de/setup
  const fromPath = /^\/help\/(en|fr|de|es)(\/|$)/.exec(pathname)?.[1] as LocaleCode | undefined;
  useEffect(() => {
    if (fromPath) setLocale(fromPath);
  }, [fromPath]);
  const choose = (l: LocaleCode) => {
    setLocale(l);
    try {
      window.localStorage.setItem(STORE_KEY, l);
    } catch {
      /* storage can be blocked */
    }
  };
  return (
    <I18nProvider locale={locale}>
      <Frame locale={locale} onLocale={choose}>
        <Routes>
          <Route index element={<Home />} />
          <Route path="privacy" element={<Doc kind="legal" slug="privacy" />} />
          <Route path="terms" element={<Doc kind="legal" slug="tos" />} />
          <Route path="dpa" element={<Doc kind="legal" slug="dpa" />} />
          <Route path="retention" element={<Doc kind="legal" slug="retention" />} />
          <Route path="portability" element={<Doc kind="legal" slug="portability" />} />
          <Route path="support" element={<Support />} />
          <Route path="help" element={<HelpIndex />} />
          <Route path="help/:lang" element={<HelpIndex />} />
          <Route path="help/:lang/:topic" element={<HelpTopic />} />
          <Route path="marketplace" element={<Marketplace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Frame>
    </I18nProvider>
  );
}

function Frame({
  locale,
  onLocale,
  children,
}: {
  locale: LocaleCode;
  onLocale: (l: LocaleCode) => void;
  children: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <div className="gp-public">
      <header className="gp-public-head">
        <Link to="/" className="gp-public-brand" aria-label="GuestPilot">
          <BrandMark size={32} />
          <span className="t-title-sm">GuestPilot</span>
        </Link>
        <nav aria-label={t("ui:public.nav.label")} className="gp-public-nav">
          <Link to={`/help/${locale}`}>{t("ui:public.nav.help")}</Link>
          <Link to="/support">{t("ui:public.nav.support")}</Link>
          <Link to="/marketplace">{t("ui:public.nav.marketplace")}</Link>
        </nav>
        <label className="gp-public-lang">
          <span className="gp-sr-only">{t("ui:public.nav.language")}</span>
          <select
            className="gp-select"
            value={locale}
            onChange={(e) => onLocale(e.target.value as LocaleCode)}
          >
            {LANGS.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
      </header>
      <main className="gp-public-main">{children}</main>
      <footer className="gp-public-foot">
        <nav aria-label={t("ui:public.nav.label")} className="gp-public-nav">
          <Link to="/privacy">{t("ui:public.nav.privacy")}</Link>
          <Link to="/terms">{t("ui:public.nav.terms")}</Link>
          <Link to="/dpa">{t("ui:public.nav.dpa")}</Link>
          <Link to="/retention">{t("ui:public.nav.retention")}</Link>
          <Link to="/portability">{t("ui:public.nav.portability")}</Link>
          <Link to={`/help/${locale}`}>{t("ui:public.nav.help")}</Link>
          <Link to="/support">{t("ui:public.nav.support")}</Link>
          <Link to="/marketplace">{t("ui:public.nav.marketplace")}</Link>
        </nav>
        <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
          © {new Date().getFullYear()} {t("ui:public.footer.rights")}
        </p>
      </footer>
    </div>
  );
}

function Home() {
  const { t, locale } = useI18n();
  const links: [string, string][] = [
    ["/privacy", "ui:public.nav.privacy"],
    ["/terms", "ui:public.nav.terms"],
    ["/marketplace", "ui:public.nav.marketplace"],
    ["/support", "ui:public.nav.support"],
    [`/help/${locale}`, "ui:public.nav.help"],
  ];
  return (
    <div className="gp-stack" style={{ gap: "var(--space-24)" }}>
      <section className="gp-public-hero">
        <h1 className="t-title-lg" style={{ margin: 0 }}>
          {t("ui:public.home.title")}
        </h1>
        <p className="t-body-lg gp-muted" style={{ margin: 0, maxWidth: "60ch" }}>
          {t("ui:public.home.lead")}
        </p>
        <div className="gp-row">
          <Link to="/marketplace" className="gp-btn gp-btn--primary gp-btn--lg">
            {t("ui:public.home.cta")}
          </Link>
          <Link to={`/help/${locale}`} className="gp-btn gp-btn--lg">
            {t("ui:public.home.ctaHelp")}
          </Link>
        </div>
        <Banner>{t("ui:public.home.inTeams")}</Banner>
      </section>
      <section className="gp-grid-3" aria-label={t("ui:public.home.title")}>
        {(["b1", "b2", "b3"] as const).map((b) => (
          <Card key={b} title={t(`ui:public.home.${b}Title`)}>
            <p className="t-body" style={{ margin: 0 }}>
              {t(`ui:public.home.${b}Body`)}
            </p>
          </Card>
        ))}
      </section>
      <p className="t-body gp-muted" style={{ margin: 0 }}>
        {t("ui:public.home.note")}
      </p>
      <section className="gp-stack" aria-labelledby="pub-links">
        <h2 id="pub-links" className="t-title-sm" style={{ margin: 0 }}>
          {t("ui:public.home.linksTitle")}
        </h2>
        <p className="t-body gp-muted" style={{ margin: 0 }}>
          {t("ui:public.home.linksLead")}
        </p>
        <ul className="gp-public-links">
          {links.map(([to, key]) => (
            <li key={to}>
              <Link to={to} className="gp-public-link">
                {t(key)}
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/** A markdown page: the legal documents (`/legal/<language>/<doc>.md`) or a help page (`/content/help/<language>/<topic>.md`). */
function Doc({ kind, slug }: { kind: "legal" | "help"; slug: string }) {
  const { t, locale } = useI18n();
  const q = useQuery({
    queryKey: ["public-doc", kind, slug, locale],
    queryFn: () =>
      loadText(
        (l) => (kind === "legal" ? `/legal/${l}/${slug}.md` : `/content/help/${l}/${slug}.md`),
        locale,
      ),
    staleTime: Infinity,
    retry: false,
  });
  return (
    <article className="gp-public-doc">
      {q.isLoading ? <Skeleton rows={8} /> : null}
      {q.isError ? <Banner tone="danger">{t("ui:public.docs.missing")}</Banner> : null}
      {q.data && q.data.lang !== locale ? <Banner>{t("ui:public.docs.englishOnly")}</Banner> : null}
      {q.data ? <Markdown source={q.data.text} /> : null}
    </article>
  );
}

function HelpIndex() {
  const { t, locale } = useI18n();
  return (
    <div className="gp-stack">
      <h1 className="t-title-lg" style={{ margin: 0 }}>
        {t("ui:public.help.title")}
      </h1>
      <p className="t-body gp-muted" style={{ margin: 0 }}>
        {t("ui:public.help.lead")}
      </p>
      <ul className="gp-public-links">
        {HELP_TOPICS.map((k) => (
          <li key={k}>
            <Link to={`/help/${locale}/${k}`} className="gp-public-link">
              {t(`ui:public.help.topics.${k}`)}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function HelpTopic() {
  const { topic } = useParams();
  const { t, locale } = useI18n();
  if (!HELP_TOPICS.includes(topic as Topic)) return <Navigate to={`/help/${locale}`} replace />;
  return (
    <div className="gp-stack">
      <Link to={`/help/${locale}`} className="t-body-strong">
        ← {t("ui:public.help.title")}
      </Link>
      <Doc kind="help" slug={topic as string} />
    </div>
  );
}

function Support() {
  const { t, locale } = useI18n();
  return (
    <div className="gp-stack">
      <h1 className="t-title-lg" style={{ margin: 0 }}>
        {t("ui:public.support.title")}
      </h1>
      <p className="t-body gp-muted" style={{ margin: 0, maxWidth: "70ch" }}>
        {t("ui:public.support.lead")}
      </p>
      <Card title={t("ui:public.support.contact")}>
        <ul className="gp-public-list">
          <li>
            {t("ui:public.support.email")} : <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>
          </li>
          <li>{t("ui:public.support.hours")}</li>
          <li>{t("ui:public.support.response")}</li>
          <li>{t("ui:public.support.urgent")}</li>
        </ul>
      </Card>
      <Card title={t("ui:public.support.includeTitle")}>
        <ul className="gp-public-list">
          {(["include1", "include2", "include3", "include4"] as const).map((k) => (
            <li key={k}>{t(`ui:public.support.${k}`)}</li>
          ))}
        </ul>
      </Card>
      <Card title={t("ui:public.support.helpTitle")}>
        <ul className="gp-public-list">
          {HELP_TOPICS.map((k) => (
            <li key={k}>
              <Link to={`/help/${locale}/${k}`}>{t(`ui:public.help.topics.${k}`)}</Link>
            </li>
          ))}
        </ul>
      </Card>
      <Card title={t("ui:public.support.privacyTitle")}>
        <p className="t-body" style={{ margin: 0 }}>
          {t("ui:public.support.privacy")} <Link to="/privacy">{t("ui:public.nav.privacy")}</Link>
        </p>
      </Card>
    </div>
  );
}

/** The address given as landing page of the Microsoft Marketplace offer: how to get GuestPilot (and where a purchase token arrives). */
function Marketplace() {
  const { t, locale } = useI18n();
  const [params] = useSearchParams();
  return (
    <div className="gp-stack">
      <h1 className="t-title-lg" style={{ margin: 0 }}>
        {t("ui:public.marketplace.title")}
      </h1>
      <p className="t-body gp-muted" style={{ margin: 0, maxWidth: "70ch" }}>
        {t("ui:public.marketplace.lead")}
      </p>
      {params.get("token") ? <Banner tone="warn">{t("ui:public.marketplace.tokenNotice")}</Banner> : null}
      <Card title={t("ui:public.marketplace.stepsTitle")}>
        <ol className="gp-public-list">
          {(["step1", "step2", "step3", "step4"] as const).map((k) => (
            <li key={k}>{t(`ui:public.marketplace.${k}`)}</li>
          ))}
        </ol>
        <p className="t-body gp-muted" style={{ margin: 0 }}>
          {t("ui:public.marketplace.pricing")}
        </p>
      </Card>
      <Card title={t("ui:public.marketplace.linksTitle")}>
        <p className="t-body" style={{ margin: 0 }}>
          {t("ui:public.marketplace.forAdmins")}
        </p>
        <ul className="gp-public-list">
          <li>
            <Link to={`/help/${locale}/permissions`}>{t("ui:public.help.topics.permissions")}</Link>
          </li>
          <li>
            <Link to="/privacy">{t("ui:public.nav.privacy")}</Link>
          </li>
          <li>
            <Link to="/terms">{t("ui:public.nav.terms")}</Link>
          </li>
          <li>
            <Link to="/support">{t("ui:public.nav.support")}</Link>
          </li>
        </ul>
      </Card>
    </div>
  );
}
